import {
  CompanyPartnerIntegration,
  IntegrationType,
  workdaySyncService,
} from '@aslanpay/portal-common-lib';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface TriggerWorkdaySyncParams {
  companyId: string;
  integrationType: Extract<IntegrationType, 'HRIS_SYNC' | 'TIMESHEET_SYNC'>;
}

interface TriggerWorkdaySyncContext {
  hasCachedIntegrations: boolean;
  previousLastSyncedAt?: string;
}

const MAX_SYNC_STATUS_REFRESH_ATTEMPTS = 5;
const SYNC_STATUS_REFRESH_DELAY_MS = 1000;

const getSyncStatusQueryKey = (companyId: string) => ['fetchCompanyIntegrations', companyId];

const getLastSyncedAt = (
  integrations: CompanyPartnerIntegration[] | undefined,
  integrationType: TriggerWorkdaySyncParams['integrationType']
) =>
  integrations
    ?.flatMap((integration) => integration.types)
    .find((type) => type.integrationType === integrationType)?.lastSyncedAt;

const delay = (duration: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, duration);
  });

const useTriggerWorkdaySync = () => {
  const queryClient = useQueryClient();

  const refetchUntilSyncStatusChanges = async (
    companyId: string,
    integrationType: TriggerWorkdaySyncParams['integrationType'],
    previousLastSyncedAt: string | undefined,
    attempt = 0
  ): Promise<void> => {
    const queryKey = getSyncStatusQueryKey(companyId);
    await queryClient.refetchQueries({ queryKey, exact: true });

    if (
      getLastSyncedAt(
        queryClient.getQueryData<CompanyPartnerIntegration[]>(queryKey),
        integrationType
      ) !== previousLastSyncedAt ||
      attempt === MAX_SYNC_STATUS_REFRESH_ATTEMPTS - 1
    ) {
      return;
    }

    await delay(SYNC_STATUS_REFRESH_DELAY_MS);
    await refetchUntilSyncStatusChanges(
      companyId,
      integrationType,
      previousLastSyncedAt,
      attempt + 1
    );
  };

  return useMutation({
    mutationFn: ({ companyId, integrationType }: TriggerWorkdaySyncParams) =>
      integrationType === 'HRIS_SYNC'
        ? workdaySyncService.syncHris(companyId)
        : workdaySyncService.syncTimesheets(companyId),
    onMutate: ({ companyId, integrationType }): TriggerWorkdaySyncContext => {
      const integrations = queryClient.getQueryData<CompanyPartnerIntegration[]>(
        getSyncStatusQueryKey(companyId)
      );

      return {
        hasCachedIntegrations: integrations !== undefined,
        previousLastSyncedAt: getLastSyncedAt(integrations, integrationType),
      };
    },
    onSettled: (_, __, { companyId, integrationType }, context) => {
      if (!context?.hasCachedIntegrations) {
        return queryClient.refetchQueries({
          queryKey: getSyncStatusQueryKey(companyId),
          exact: true,
        });
      }

      return refetchUntilSyncStatusChanges(
        companyId,
        integrationType,
        context.previousLastSyncedAt
      );
    },
  });
};

export default useTriggerWorkdaySync;
